import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { datadogRum } from '@datadog/browser-rum';
import type SessionContextService from 'tio-employee/services/session-context';
import type ZendeskService from 'tio-employee/services/zendesk';
import type RouterService from '@ember/routing/router-service';
import type PartnerService from 'tio-employee/services/partner';
import type SessionService from 'tio-employee/services/session';
import type FeaturesService from 'tio-common/services/features';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type GoogleAnalyticsService from 'tio-employee/services/google-analytics';
import type CableService from 'tio-common/services/cable';
import type TelemetryService from 'tio-common/services/telemetry';
import type Transition from '@ember/routing/transition';
import ENV from 'tio-employee/config/environment';
import type UserModel from 'tio-common/models/user';
import type WellnessTrainRecalculatorService from 'tio-common/services/wellness-train-recalculator';
import { wellnessInfoRequest } from 'tio-common/requests/user';

function setupForethought(
  firstName: string,
  lastName: string,
  fullName: string,
  jwt: string,
  email = '',
  partnerName = ''
) {
  document.querySelector('#forethought-chat')?.remove();
  const script = document.createElement('script');
  script.setAttribute('src', 'https://solve-widget.forethought.ai/embed.js');
  script.setAttribute('id', 'forethought-widget-embed-script');
  script.setAttribute('data-api-key', '21bf8592-1b59-4758-84ef-f82c33c6462e');

  script.setAttribute('data-ft-First-Name', firstName);
  script.setAttribute('data-ft-Last-Name', lastName);
  script.setAttribute('data-ft-Full-Name', fullName);
  script.setAttribute('data-ft-zendesk-jwt', jwt);
  script.setAttribute('data-ft-Email', email);
  script.setAttribute('data-ft-Partner-Name', partnerName);
  script.setAttribute('data-ft-workflow-tag', ENV.environment);
  document.head.append(script);
}

export default class AuthenticatedRoute extends Route {
  @service declare session: SessionService;
  @service declare sessionContext: SessionContextService;
  @service declare features: FeaturesService;
  @service declare zendesk: ZendeskService;
  @service declare router: RouterService;
  @service declare partner: PartnerService;
  @service declare store: StoreService;
  @service declare googleAnalytics: GoogleAnalyticsService;
  @service declare cable: CableService;
  @service declare wellnessTrainRecalculator: WellnessTrainRecalculatorService;
  @service declare telemetry: TelemetryService;

  async beforeModel(transition: Transition) {
    const loggedIn = this.session.requireAuthentication(transition, 'login');

    if (!loggedIn) {
      return;
    }

    // Configure WebSocket connection for ActionCable
    const token = this.session.data.authenticated?.access_token;
    if (token && !this.cable.isConfigured) {
      this.cable.configure(ENV.apiHost, ENV.apiKey, token, ENV.wsapiHost);
    }

    if (!this.sessionContext.isLoaded) {
      try {
        await this.sessionContext.load();
      } catch (e) {
        console.error('[route:authenticated] Could not load session context', e);
        this.sessionContext.logout();
        return;
      }
    }

    if (!this.sessionContext.user?.id) {
      console.error(
        '[route:authenticated] Missing user id in session context; redirecting to login'
      );
      this.sessionContext.logout();
      return;
    }

    try {
      this.zendesk.setVisibilityForEnvironment();
      this.zendesk.setWidgetLocale();
    } catch (e) {
      console.error('[route:authenticated] Could not set zendesk user', e);
    }

    try {
      if (this.partner.isZendeskEnabled) {
        // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
        const zendeskJwt = await this.store.adapterFor('user').getZendeskJwt();

        setupForethought(
          this.sessionContext.user.person?.firstName,
          this.sessionContext.user.person?.lastName,
          this.sessionContext.user.person?.fullName,
          zendeskJwt.token,
          this.sessionContext.user.employees?.[0]?.email,
          this.sessionContext.user.employees?.[0]?.company.name
        );
        window.addEventListener('message', (e) => {
          if (e.data.event === 'forethoughtWidgetIntegrationHandoff') {
            this.zendesk.zendeskAction('messenger', 'resetWidget');
          }
        });
      }
    } catch (e) {
      // This is a non-blocking error. If Forethought fails to load, it shouldn't prevent the rest of the app from loading.
      console.error('[route:authenticated] Could not setup Forethought chat', e);
    }

    try {
      this.setupDatadogIdentity();
    } catch (e) {
      console.error('Error setting up DataDog user', e);
    }

    try {
      this.setupDatadogPrivateContext();
    } catch (e) {
      console.error('Error setting up DataDog private context', e);
    }

    // Mirror user identity to custom telemetry
    try {
      const currentRole = this.sessionContext.currentRole;
      this.telemetry.setUser({
        id: this.sessionContext.user.id,
        name: this.sessionContext.user.person?.fullName,
        role: currentRole?.role,
        relationshipType: currentRole?.relationshipType,
      });

      this.telemetry.setContext({
        company_id: this.sessionContext.company?.id ?? undefined,
      });
    } catch (e) {
      console.error('Error setting up telemetry user', e);
    }

    // Checks to see if a route is saved in the session and transition to it
    this.session.transitionToRouteAfterAuthentication();

    // Check if landing on root route - either via URL (/) or named route transition (authenticated/authenticated.index)
    // @ts-expect-error: transition.intent may have url (URL transition) or name (named route transition)
    const isRootUrlTransition = transition.intent?.url === '/';
    const targetRouteName = transition.to?.name;
    const isRootNamedTransition =
      targetRouteName === 'authenticated.index' || targetRouteName === 'authenticated';

    if (isRootUrlTransition || isRootNamedTransition) {
      if (this.sessionContext.currentRole?.relationshipType === 'PARTICIPANT') {
        this.router.transitionTo('authenticated.dashboard');
      } else {
        this.router.transitionTo('authenticated.admin');
      }
    }
  }

  async model() {
    const userId = this.sessionContext.user?.id;
    if (!userId) {
      console.error('[route:authenticated] Missing user id in model hook; redirecting to login');
      this.router.transitionTo('login');
      return {
        user: this.sessionContext.user as unknown as UserModel,
        wellnessData: null,
      };
    }
    const user = await this.store.findRecord<UserModel>('user', userId);

    let wellnessData: Record<string, unknown> | null = null;
    try {
      await this.wellnessTrainRecalculator.recalculate.perform();
      const response = await this.store.request(wellnessInfoRequest(userId));
      wellnessData = response.content as Record<string, unknown>;
    } catch (e) {
      console.error('[route:authenticated] Failed to load wellness train:', e);
    }

    return { user, wellnessData };
  }

  afterModel() {
    // Initialize Google Analytics tracking now that user is fully authenticated
    // and currentRole is available
    this.googleAnalytics.initializeTracking();

    // Track this route transition with user context
    const currentRole = this.sessionContext.currentRole;
    this.googleAnalytics.trackRouteTransition('authenticated', {
      user_authenticated: true,
      role_type: currentRole?.relationshipType,
      company_id: currentRole?.company?.id,
    });

    // Note: Wellness train data is now loaded in model() hook
  }

  setupDatadogIdentity() {
    const { user } = this.sessionContext;
    const currentRole = this.sessionContext.currentRole;

    datadogRum.setUser({
      // @ts-expect-error: not sure
      id: user.id,
      name: user.person?.fullName,
      role: currentRole?.role,
      relationshipType: currentRole?.relationshipType,
    });
  }

  setupDatadogPrivateContext() {
    const { user } = this.sessionContext;
    const currentRole = this.sessionContext.currentRole;

    if (user?.id) {
      datadogRum.setGlobalContextProperty('tio.user.id', user.id);
    }
    if (user?.person?.fullName) {
      datadogRum.setGlobalContextProperty('tio.user.name', user.person.fullName);
    }
    if (currentRole?.company?.id) {
      datadogRum.setGlobalContextProperty('tio.partner.id', currentRole.company.id);
    }
    if (currentRole?.company?.name) {
      datadogRum.setGlobalContextProperty('tio.partner.name', currentRole.company.name);
    }
  }
}
